import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import EditorialMarquee from "@/components/site/Marquee";
import { PILLARS } from "@/constants/site";

const Program = () => {
  useEffect(() => {
    document.title = "TWGAS · The Program";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="bg-[#0A0A0A]">
      {/* Header */}
      <section className="pt-40 md:pt-56 pb-16 md:pb-24 border-b border-white/10">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-8">
            <div className="num-tag mb-4">/ THE PROGRAM · SYSTEM MAP</div>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-6xl md:text-8xl lg:text-[10rem] leading-[0.86] tracking-tight"
            >
              A system,
              <br />
              <span className="stroke-fire">not a service.</span>
            </motion.h1>
          </div>
          <div className="md:col-span-4 flex items-end">
            <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-[42ch]">
              TWGAS is engineered around four pillars — each one designed to
              compound on the others. Together, they build athletes who are
              impossible to overlook.
            </p>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* Full pillar breakdown */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 space-y-24 md:space-y-32">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-120px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className={`grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start ${
                i % 2 === 1 ? "md:[direction:rtl]" : ""
              }`}
            >
              <div className="md:col-span-6 md:[direction:ltr]">
                <div className="relative overflow-hidden border border-white/10">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-[360px] md:h-[520px] object-cover grayscale hover:grayscale-0 transition-[filter] duration-[900ms]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 chip">
                    <span className="dot" />
                    {p.tag}
                  </div>
                  <div className="absolute bottom-4 right-4 font-display text-white/70 text-[100px] leading-none">
                    {p.num}
                  </div>
                </div>
              </div>

              <div className="md:col-span-6 md:[direction:ltr]">
                <div className="num-tag mb-4">/ Pillar {p.num}</div>
                <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
                  {p.title}
                </h2>
                <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-[54ch]">
                  {p.lead}
                </p>
                <ul className="mt-8 border-t border-white/10">
                  {p.bullets.map((b, j) => (
                    <li
                      key={b}
                      className="flex items-baseline gap-4 md:gap-6 border-b border-white/10 py-4 md:py-5"
                    >
                      <span className="font-mono-plex text-[10px] tracking-[0.28em] text-zinc-500">
                        /0{j + 1}
                      </span>
                      <span className="font-display uppercase text-xl md:text-2xl tracking-wide">
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-rule py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <h3 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
            Enough reading.
            <br />
            <span className="stroke-fire">Start working.</span>
          </h3>
          <Link to="/contact" className="btn-fire">
            Apply Now
            <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Program;
